/**
 * Configuración → Cuentas bancarias (`/configuracion/cuentas-bancarias`).
 *
 * Puerto del CRUD de `Bank_accounts` + `views/bank_accounts/index.php`: banco,
 * descripción, número, moneda, CCI, saldo inicial y si la cuenta se muestra en
 * los documentos.
 *
 * La pantalla de **Movimientos** de la cuenta (`Bank_accounts::movements`, con
 * sus exportaciones a Excel y PDF) sigue en el panel anterior: desde aquí se
 * enlaza a ella. Por eso el borrado sólo se permite si la cuenta no tiene
 * movimientos (la API responde 409 y aquí se explica).
 */

import { ArrowLeft, ExternalLink, Landmark, Pencil, Plus, ShieldAlert, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Alert, ICONO_EN_ALERTA } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { ConfirmDialog, Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Select } from '@/components/ui/select';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { ApiError } from '@/lib/http/api-error';
import { buildLegacyUrl } from '@/lib/legacy-url';
import { formatCurrency } from '@/lib/utils';

import { useBancos, useCuentasBancarias, useMonedas, useMutarCuentas } from '../catalogos.api';
import type { CuentaBancaria, CuentaPayload } from '../catalogos.types';

/** Mensaje legible de un error (de la API o de red). */
function mensajeDe(error: unknown, porDefecto: string): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;

  return porDefecto;
}

const FORMULARIO_VACIO: CuentaPayload = {
  banco_id: 0,
  moneda_id: '',
  descripcion: '',
  numero: '',
  cci: '',
  saldo_inicial: 0,
  mostrar_en_documentos: true,
};

/** Alta/edición de una cuenta bancaria. */
function CuentaDialogo({
  cuenta,
  bancos,
  monedas,
  guardando,
  error,
  onGuardar,
  onCerrar,
}: {
  cuenta: CuentaBancaria | null;
  bancos: { id: number; descripcion: string }[];
  monedas: { codigo: string; descripcion: string; simbolo: string }[];
  guardando: boolean;
  error: string | null;
  onGuardar: (payload: CuentaPayload) => void;
  onCerrar: () => void;
}) {
  const [datos, setDatos] = useState<CuentaPayload>(
    cuenta
      ? {
          banco_id: cuenta.banco_id,
          moneda_id: cuenta.moneda_id,
          descripcion: cuenta.descripcion,
          numero: cuenta.numero,
          cci: cuenta.cci,
          saldo_inicial: cuenta.saldo_inicial,
          mostrar_en_documentos: cuenta.mostrar_en_documentos,
        }
      : {
          ...FORMULARIO_VACIO,
          banco_id: bancos[0]?.id ?? 0,
          moneda_id: monedas[0]?.codigo ?? '',
        },
  );

  const cambiar = <K extends keyof CuentaPayload,>(campo: K, valor: CuentaPayload[K]) =>
    setDatos((previo) => ({ ...previo, [campo]: valor }));

  const valido =
    datos.banco_id > 0 &&
    datos.moneda_id !== '' &&
    datos.descripcion.trim().length > 0 &&
    datos.numero.trim().length > 0;

  return (
    <Dialog
      open
      onClose={onCerrar}
      title={cuenta ? `Editar cuenta #${cuenta.id}` : 'Nueva cuenta bancaria'}
      description="Los datos de la cuenta se usan en los pagos, los depósitos y los comprobantes."
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onCerrar} disabled={guardando}>
            Cancelar
          </Button>
          <Button
            size="sm"
            loading={guardando}
            disabled={!valido}
            onClick={() =>
              onGuardar({
                ...datos,
                descripcion: datos.descripcion.trim(),
                numero: datos.numero.trim(),
                cci: datos.cci.trim(),
              })
            }
          >
            Guardar
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? <Alert variant="error" title="No se pudo guardar">{error}</Alert> : null}

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="cuenta-banco" className="text-sm font-medium text-ink-700">
              Banco
            </label>
            <Select
              id="cuenta-banco"
              className="h-10 w-full rounded-control border border-ink-200 bg-white px-2.5 text-sm text-ink-800 focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none"
              value={String(datos.banco_id)}
              onChange={(evento) => cambiar('banco_id', Number(evento.target.value))}
            >
              {bancos.map((banco) => (
                <option key={banco.id} value={banco.id}>
                  {banco.descripcion.trim()}
                </option>
              ))}
            </Select>
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="cuenta-moneda" className="text-sm font-medium text-ink-700">
              Moneda
            </label>
            <Select
              id="cuenta-moneda"
              className="h-10 w-full rounded-control border border-ink-200 bg-white px-2.5 text-sm text-ink-800 focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none"
              value={datos.moneda_id}
              onChange={(evento) => cambiar('moneda_id', evento.target.value)}
            >
              {monedas.map((moneda) => (
                <option key={moneda.codigo} value={moneda.codigo}>
                  {moneda.codigo} · {moneda.descripcion}
                </option>
              ))}
            </Select>
          </div>
        </div>

        <Input
          label="Descripción"
          value={datos.descripcion}
          maxLength={255}
          autoFocus
          placeholder="Ej. SOLES BCP"
          onChange={(evento) => cambiar('descripcion', evento.target.value)}
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="Número de cuenta"
            value={datos.numero}
            maxLength={255}
            placeholder="Ej. 4654646356"
            onChange={(evento) => cambiar('numero', evento.target.value)}
          />

          <Input
            label="CCI"
            value={datos.cci}
            maxLength={255}
            hint="Opcional (cuenta interbancaria)"
            onChange={(evento) => cambiar('cci', evento.target.value)}
          />
        </div>

        <Input
          label="Saldo inicial"
          type="number"
          min={0}
          step={0.01}
          value={String(datos.saldo_inicial)}
          rightSlot={<span className="px-3 text-xs text-ink-400">S/</span>}
          hint="Saldo con el que se abre la cuenta en el sistema"
          onChange={(evento) => {
            const bruto = evento.target.value;
            cambiar('saldo_inicial', bruto === '' ? 0 : Number(bruto));
          }}
        />

        <Checkbox
          label="Mostrar en los documentos"
          hint="Aparece como cuenta de la empresa en los comprobantes"
          checked={datos.mostrar_en_documentos}
          onChange={(evento) => cambiar('mostrar_en_documentos', evento.target.checked)}
        />
      </div>
    </Dialog>
  );
}

export function CuentasBancariasPage() {
  useDocumentTitle('Configuración · Cuentas bancarias');

  const cuentas = useCuentasBancarias();
  const bancos = useBancos();
  const monedas = useMonedas();
  const mutaciones = useMutarCuentas();

  const [dialogo, setDialogo] = useState<{ abierto: boolean; cuenta: CuentaBancaria | null }>({
    abierto: false,
    cuenta: null,
  });
  const [porEliminar, setPorEliminar] = useState<CuentaBancaria | null>(null);
  const [aviso, setAviso] = useState<{ tono: 'success' | 'error'; texto: string } | null>(null);
  const [errorDialogo, setErrorDialogo] = useState<string | null>(null);

  const sinPermiso = cuentas.error instanceof ApiError && cuentas.error.status === 403;
  const lista = cuentas.data?.cuentas ?? [];
  const listaBancos = bancos.data?.bancos ?? [];
  const listaMonedas = monedas.data?.monedas ?? [];
  const sinCatalogos = listaBancos.length === 0 || listaMonedas.length === 0;
  /** Los movimientos de la cuenta siguen en el panel anterior. */
  const urlMovimientos = buildLegacyUrl('Cuentas/Movimientos');

  const cabecera = (
    <header className="flex flex-wrap items-start justify-between gap-4">
      <div>
        <Link
          to="/configuracion"
          className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-ink-400 transition-colors hover:text-brand-600"
        >
          <ArrowLeft className="size-3" aria-hidden="true" />
          Configuración
        </Link>
        <h1 className="mt-1 flex items-center gap-2 text-2xl font-bold tracking-tight text-ink-900">
          <Landmark className="size-6 text-brand-500" aria-hidden="true" />
          Cuentas bancarias
        </h1>
        <p className="mt-1 max-w-2xl text-sm text-ink-500">
          Cuentas de la empresa para cobros, depósitos y comprobantes.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {urlMovimientos ? (
          <a
            href={urlMovimientos}
            target="_blank"
            rel="noreferrer"
            className="inline-flex h-9 items-center gap-1.5 rounded-control border border-ink-200 bg-white px-3.5 text-sm font-semibold text-ink-800 transition-colors hover:border-brand-300 hover:text-brand-700"
          >
            Movimientos
            <ExternalLink className="size-3.5" aria-hidden="true" />
          </a>
        ) : null}
        <Button
          size="sm"
          leftIcon={<Plus className="size-3.5" aria-hidden="true" />}
          disabled={sinPermiso || sinCatalogos}
          title={sinCatalogos ? 'Necesitas al menos un banco y una moneda' : undefined}
          onClick={() => {
            setErrorDialogo(null);
            setDialogo({ abierto: true, cuenta: null });
          }}
        >
          Nueva cuenta
        </Button>
      </div>
    </header>
  );

  if (sinPermiso) {
    return (
      <div className="space-y-6">
        {cabecera}
        <Alert variant="warning" title="Acceso restringido">
          <span className={ICONO_EN_ALERTA}>
            <ShieldAlert className="size-4" aria-hidden="true" />
            Esta pantalla es del grupo <strong>Configuración</strong>.
          </span>
        </Alert>
      </div>
    );
  }

  const guardar = (payload: CuentaPayload) => {
    setErrorDialogo(null);

    if (dialogo.cuenta) {
      mutaciones.actualizar.mutate(
        { id: dialogo.cuenta.id, payload },
        {
          onSuccess: (datos) => {
            setDialogo({ abierto: false, cuenta: null });
            setAviso({ tono: 'success', texto: datos.mensaje });
          },
          onError: (error) => setErrorDialogo(mensajeDe(error, 'No se pudo guardar la cuenta.')),
        },
      );
      return;
    }

    mutaciones.crear.mutate(payload, {
      onSuccess: (datos) => {
        setDialogo({ abierto: false, cuenta: null });
        setAviso({ tono: 'success', texto: datos.mensaje });
      },
      onError: (error) => setErrorDialogo(mensajeDe(error, 'No se pudo guardar la cuenta.')),
    });
  };

  const eliminar = () => {
    if (!porEliminar) return;

    mutaciones.eliminar.mutate(porEliminar.id, {
      onSuccess: (datos) => {
        setPorEliminar(null);
        setAviso({ tono: 'success', texto: datos.mensaje });
      },
      onError: (error) => {
        setPorEliminar(null);
        setAviso({ tono: 'error', texto: mensajeDe(error, 'No se pudo eliminar la cuenta.') });
      },
    });
  };

  return (
    <div className="space-y-6">
      {cabecera}

      {aviso ? (
        <Alert variant={aviso.tono === 'success' ? 'success' : 'error'}>{aviso.texto}</Alert>
      ) : null}

      {sinCatalogos && !cuentas.isLoading && !bancos.isLoading && !monedas.isLoading ? (
        <Alert variant="warning" title="Faltan catálogos">
          Para registrar una cuenta necesitas al menos un <strong>banco</strong> y una{' '}
          <strong>moneda</strong>: revísalos en <Link to="/configuracion/bancos">Bancos</Link> y{' '}
          <Link to="/configuracion/monedas">Monedas</Link>.
        </Alert>
      ) : null}

      {cuentas.isLoading ? (
        <Skeleton className="h-64 w-full" />
      ) : cuentas.error && !sinPermiso ? (
        <Alert variant="error" title="No se pudieron leer las cuentas">
          {mensajeDe(cuentas.error, 'La API no respondió.')}
        </Alert>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>Listado de cuentas bancarias</CardTitle>
            <Badge variant="neutral">{cuentas.data?.total ?? 0} registradas</Badge>
          </CardHeader>
          <CardContent>
            {lista.length === 0 ? (
              <p className="py-6 text-center text-sm text-ink-500">
                Todavía no hay cuentas registradas.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[52rem] border-collapse text-sm">
                  <thead>
                    <tr className="text-left text-[11px] font-semibold uppercase tracking-wide text-ink-400">
                      <th className="w-12 pb-2">#</th>
                      <th className="pb-2">Banco</th>
                      <th className="pb-2">Descripción</th>
                      <th className="pb-2">Número</th>
                      <th className="pb-2">CCI</th>
                      <th className="w-24 pb-2">Moneda</th>
                      <th className="w-32 pb-2 text-right">Saldo inicial</th>
                      <th className="w-28 pb-2">Documentos</th>
                      <th className="w-24 pb-2 text-right">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lista.map((cuenta) => (
                      <tr key={cuenta.id} className="border-t border-ink-100">
                        <td className="py-3 text-ink-500">{cuenta.id}</td>
                        <td className="py-3 font-medium text-ink-900">{cuenta.banco.trim()}</td>
                        <td className="py-3 text-ink-800">{cuenta.descripcion}</td>
                        <td className="py-3 font-mono text-xs text-ink-700">{cuenta.numero}</td>
                        <td className="py-3 font-mono text-xs text-ink-500">{cuenta.cci || '—'}</td>
                        <td className="py-3">
                          <Badge variant="outline">{cuenta.moneda_id}</Badge>
                        </td>
                        <td
                          className="py-3 text-right text-ink-800"
                          title={formatCurrency(cuenta.saldo_inicial)}
                        >
                          {formatCurrency(cuenta.saldo_inicial)}
                        </td>
                        <td className="py-3">
                          <Badge variant={cuenta.mostrar_en_documentos ? 'brand' : 'neutral'}>
                            {cuenta.mostrar_en_documentos ? 'Visible' : 'Oculta'}
                          </Badge>
                        </td>
                        <td className="py-3">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              aria-label={`Editar ${cuenta.descripcion}`}
                              title="Editar"
                              onClick={() => {
                                setErrorDialogo(null);
                                setDialogo({ abierto: true, cuenta });
                              }}
                            >
                              <Pencil className="size-4" aria-hidden="true" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              aria-label={`Eliminar ${cuenta.descripcion}`}
                              title="Eliminar"
                              onClick={() => setPorEliminar(cuenta)}
                            >
                              <Trash2 className="size-4 text-rose-500" aria-hidden="true" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {dialogo.abierto ? (
        <CuentaDialogo
          key={`cuenta-${dialogo.cuenta?.id ?? 'nueva'}`}
          cuenta={dialogo.cuenta}
          bancos={listaBancos}
          monedas={listaMonedas}
          guardando={mutaciones.crear.isPending || mutaciones.actualizar.isPending}
          error={errorDialogo}
          onGuardar={guardar}
          onCerrar={() => setDialogo({ abierto: false, cuenta: null })}
        />
      ) : null}

      <ConfirmDialog
        open={porEliminar !== null}
        title="¿Eliminar la cuenta bancaria?"
        description={`Se eliminará «${porEliminar?.descripcion ?? ''}». Las cuentas con movimientos no se pueden eliminar.`}
        confirmLabel="Sí, eliminar"
        loading={mutaciones.eliminar.isPending}
        onClose={() => setPorEliminar(null)}
        onConfirm={eliminar}
      />
    </div>
  );
}
